import { motion } from "framer-motion";
import { ArrowLeft, Shield, FileText, CheckCircle, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const LegalDisclaimerPage = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="legal-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
            data-testid="back-to-home"
          >
            <ArrowLeft size={18} />
            <span>{t("legal.backToHome")}</span>
          </Link>
          <h1 className="font-['Syne'] text-3xl md:text-4xl font-bold uppercase flex items-center gap-3">
            <Shield className="text-[#D4AF37]" size={32} />
            {t("legal.pageTitle")}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Age Verification Notice */}
          <div className="bg-red-500/10 border border-red-500/30 p-6 flex items-start gap-4">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="font-['Syne'] text-lg font-bold text-red-400 mb-2">
                {language === "tr" ? "18+ Yaş Uyarısı" : "18+ Age Warning"}
              </h2>
              <p className="text-gray-300 text-sm">
                {language === "tr" 
                  ? "Bu web sitesi ve sunulan hizmetler SADECE 18 yaş ve üzeri reşit bireylere yöneliktir. Siteyi kullanarak 18 yaşından büyük olduğunuzu kabul etmiş sayılırsınız."
                  : "This website and services offered are intended ONLY for adults aged 18 and over. By using this site, you confirm that you are over 18 years of age."
                }
              </p>
            </div>
          </div>

          {/* Main Disclaimer Box */}
          <div className="bg-white/5 border border-white/10 p-8 md:p-10">
            <div className="flex items-start gap-4 mb-6">
              <FileText className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-['Syne'] text-xl font-bold uppercase mb-2 text-[#D4AF37]">
                  {language === "tr" ? "Hizmet Kapsamı ve Yasal Uyumluluk" : "Service Scope and Legal Compliance"}
                </h2>
              </div>
            </div>
            
            <div className="text-gray-300 leading-relaxed space-y-4 text-base">
              <p>{t("legal.fullDisclaimer")}</p>
            </div>
          </div>

          {/* Key Points */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="font-['Syne'] text-lg font-bold mb-4 text-white flex items-center gap-2">
                <span className="w-8 h-8 bg-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                  <CheckCircle size={18} />
                </span>
                {language === "tr" ? "Sunduğumuz Hizmetler" : "Services We Provide"}
              </h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] mt-1">•</span>
                  {language === "tr" ? "Konsomatris hizmeti (otel, villa, rezidans)" : "Hostess service (hotel, villa, residence)"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] mt-1">•</span>
                  {language === "tr" ? "Dansöz ve oryantal hizmeti (özel etkinlikler)" : "Dancer and belly dance service (special events)"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] mt-1">•</span>
                  {language === "tr" ? "VIP taksi ve transfer hizmeti" : "VIP taxi and transfer service"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] mt-1">•</span>
                  {language === "tr" ? "Mekan hizmeti (direk dansı, striptiz showları)" : "Venue service (pole dance, striptease shows)"}
                </li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="font-['Syne'] text-lg font-bold mb-4 text-white flex items-center gap-2">
                <span className="w-8 h-8 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
                  <Shield size={18} />
                </span>
                {language === "tr" ? "Yasal Uyumluluk" : "Legal Compliance"}
              </h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  {language === "tr" ? "Tüm hizmetler KKTC yasalarına uygundur" : "All services comply with TRNC laws"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  {language === "tr" ? "Hizmetler 18+ reşit bireyler arasındadır" : "Services are between 18+ consenting adults"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  {language === "tr" ? "Müşteri gizliliği tam koruma altındadır" : "Customer privacy is fully protected"}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  {language === "tr" ? "Yasadışı faaliyet desteklenmez" : "Illegal activity is not supported"}
                </li>
              </ul>
            </div>
          </div>

          {/* Privacy Policy Summary */}
          <div className="bg-[#00FFFF]/5 border border-[#00FFFF]/20 p-6">
            <h3 className="font-['Syne'] text-lg font-bold mb-3 text-[#00FFFF]">
              {language === "tr" ? "Gizlilik Politikası" : "Privacy Policy"}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {language === "tr" 
                ? "Müşteri bilgileri kesinlikle gizli tutulmakta ve üçüncü şahıslarla paylaşılmamaktadır. Tüm kişisel veriler güvenli şekilde saklanmakta ve yasal gereklilikler dışında hiçbir kurum veya kişiyle paylaşılmamaktadır."
                : "Customer information is kept strictly confidential and is not shared with third parties. All personal data is stored securely and is not shared with any institution or person except for legal requirements."
              }
            </p>
          </div>

          {/* Contact Info */}
          <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 p-6 text-center">
            <p className="text-gray-300 mb-2">
              {language === "tr" 
                ? "Sorularınız için bize ulaşın:" 
                : "Contact us for questions:"}
            </p>
            <a 
              href="tel:+905488484828" 
              className="text-[#D4AF37] font-bold text-2xl hover:text-white transition-colors"
            >
              +90 548 848 4828
            </a>
            <p className="text-gray-500 text-sm mt-2">
              {language === "tr" ? "7/24 WhatsApp & Telefon" : "24/7 WhatsApp & Phone"}
            </p>
          </div>

          {/* Last Updated */}
          <p className="text-center text-gray-600 text-sm">
            {language === "tr" 
              ? `Son güncelleme: ${new Date().toLocaleDateString("tr-TR")}` 
              : `Last updated: ${new Date().toLocaleDateString("en-US")}`}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LegalDisclaimerPage;
